<template>
  <div class="animal-journey-game">
    <LevelComplete
      v-if="isGameComplete"
      message="Awesome!"
    />
    <div v-else>
      <h1 class="word-image" @click="speakOutLoud(targetWord)">
        {{ wordEmoji }}
      </h1>
      <p v-if="message" class="message">
        {{ message }}
      </p>

      <!-- Display the word with underscores and revealed letters -->
      <div class="word-display">
        <span
          v-for="(letter, index) in targetWordArray"
          :key="index"
          class="letter-slot"
        >
          {{ guessedLetters[letter] === true ? letter : "*" }}
        </span>
      </div>

      <!-- Animal Journey Display -->
      <div class="animal-journey-display">
        <pre>{{ animalJourneyStage }}</pre>
      </div>

      <!-- Letter buttons -->
      <div class="letter-buttons">
        <button
          v-for="letter in alphabet"
          :key="letter"
          :disabled="letter in guessedLetters || gameOver"
          :class="{
            'correct-letter': guessedLetters[letter] === true,
            'incorrect-letter': guessedLetters[letter] === false,
          }"
          @click="guessLetter(letter)"
        >
          {{ letter }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import LevelComplete from "../LevelComplete.vue";
import { speechMixin } from "@/mixins/speechMixin"; // update the path as needed

export default {
  mixins: [speechMixin],
  components: {
    LevelComplete,
  },
  data() {
    return {
      words: [
        {
          word: "HOUSE",
          emoji: "🏠",
          startEmoji: "🐛", // Caterpillar
          endEmoji: "🍃", // Leaf
          startName: "caterpillar",
          endName: "leaf",
        },
        {
          word: "BANANA",
          emoji: "🍌",
          startEmoji: "🐒", // Monkey
          endEmoji: "🍌", // Banana
          startName: "monkey",
          endName: "banana",
        },
        {
          word: "GRAPE",
          emoji: "🍇",
          startEmoji: "🐝", // Bee
          endEmoji: "🌸", // Flower
          startName: "bee",
          endName: "flower",
        },
        {
          word: "WATERMELON",
          emoji: "🍉",
          startEmoji: "🐿️", // Squirrel
          endEmoji: "🌰", // Nut
          startName: "squirrel",
          endName: "nut",
        },
        {
          word: "STRAWBERRY",
          emoji: "🍓",
          startEmoji: "🐄", // Cow
          endEmoji: "🌿", // Grass
          startName: "cow",
          endName: "grass",
        },
      ],
      targetWord: "",
      targetWordArray: [],
      wordEmoji: "",
      guessedLetters: {},
      correctGuesses: 0,
      message: "",
      isGameComplete: false,
      completedWords: new Set(),
      alphabet: "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split(""),
      startEmoji: "",
      endEmoji: "",
      startName: "",
      endName: "",
    };
  },
  computed: {
    gameOver() {
      return this.isWordGuessed;
    },
    animalJourneyStage() {
      const totalSteps = this.uniqueLettersCount;
      const stepsLeft = totalSteps - this.correctGuesses;
      const path = ".".repeat(stepsLeft);
      return `${this.startEmoji}${path}${this.endEmoji}`;
    },
    isWordGuessed() {
      return this.targetWordArray.every(
        (letter) => this.guessedLetters[letter] === true
      );
    },
    uniqueLetters() {
      return [...new Set(this.targetWordArray)];
    },
    uniqueLettersCount() {
      return this.uniqueLetters.length;
    },
    correctlyGuessedUniqueLettersCount() {
      return this.uniqueLetters.filter(
        (letter) => this.guessedLetters[letter] === true
      ).length;
    },
  },
  mounted() {
    // Initialize voices from the mixin
    window.speechSynthesis.onvoiceschanged = this.setupVoices;
    this.setupVoices();
    this.setupGame();
  },
  methods: {
    setupGame() {
      // Clear the message when a new game starts
      this.message = "";

      if (this.completedWords.size === this.words.length) {
        this.isGameComplete = true;
        this.speakOutLoud("Awesome!");
        setTimeout(() => {
          this.$router.push("/words");
        }, 4000);
        return;
      }

      const availableWords = this.words.filter(
        (wordObj) => !this.completedWords.has(wordObj.word)
      );

      if (availableWords.length > 0) {
        const randomIndex = Math.floor(Math.random() * availableWords.length);
        const selectedWordObj = availableWords[randomIndex];
        this.targetWord = selectedWordObj.word;
        this.targetWordArray = this.targetWord.split("");
        this.wordEmoji = selectedWordObj.emoji;
        this.startEmoji = selectedWordObj.startEmoji;
        this.endEmoji = selectedWordObj.endEmoji;
        this.startName = selectedWordObj.startName;
        this.endName = selectedWordObj.endName;
        this.speakOutLoud(this.targetWord)
      } else {
        console.error("No available words to select.");
        return;
      }

      this.guessedLetters = {};
      this.correctGuesses = 0;
    },
    guessLetter(letter) {
      if (letter in this.guessedLetters) {
        return; // Letter already guessed
      }

      if (this.targetWordArray.includes(letter)) {
        // Correct guess
        this.guessedLetters[letter] = true;
        // Speak the letter using the mixin method
        this.speakOutLoud(letter.toLowerCase());
        this.correctGuesses = this.correctlyGuessedUniqueLettersCount;

        if (this.isWordGuessed) {
          this.playSound(
            "https://commondatastorage.googleapis.com/codeskulptor-assets/week7-brrring.m4a"
          );
          this.message = `🎉 Well done!`;
          this.completedWords.add(this.targetWord);

          // Wait for 3 seconds before starting a new game
          setTimeout(() => {
            this.setupGame();
          }, 3000);
        }
      } else {
        // Incorrect guess
        this.guessedLetters[letter] = false;
        this.message = "Incorrect!";

        // Clear message after 3 seconds
        setTimeout(() => {
          this.message = "";
        }, 3000);
      }
    },
    playSound(url) {
      const audio = new Audio(url);
      audio.play();
    },
  },
};
</script>

<style scoped>
.animal-journey-game {
  text-align: center;
}

.word-image {
  font-size: 4rem;
  color: #ff4081;
  text-align: center;
  cursor: pointer;
}

.message {
  font-size: 1.5rem;
  font-weight: bold;
  text-align: center;
  margin-top: 10px;
}

/* Word display styles */
.word-display {
  display: flex;
  justify-content: center;
  margin: 20px 0;
}

.letter-slot {
  width: 40px;
  height: 60px;
  border-bottom: 3px solid #333;
  font-size: 2rem;
  text-transform: uppercase;
  text-align: center;
  margin: 0 5px;
}

/* Animal Journey display styles */
.animal-journey-display {
  font-size: 2rem;
  line-height: 1;
  margin: 20px 0;
  white-space: pre-wrap; /* Preserve newlines */
}

/* Letter buttons */
.letter-buttons {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(40px, 1fr));
  max-width: 500px;
  margin: 0 auto;
  gap: 5px;
}

.letter-buttons button {
  padding: 10px;
  font-size: 1.5rem;
  background-color: #81d4fa;
  border: none;
  border-radius: 5px;
  cursor: pointer;
}

/* Correct and incorrect letters with higher specificity */
.letter-buttons button.correct-letter:disabled {
  background-color: lightgreen;
  cursor: not-allowed;
}

.letter-buttons button.incorrect-letter:disabled {
  background-color: #ff4081;
  cursor: not-allowed;
}

.letter-buttons button:disabled {
  background-color: #cfd8dc;
  cursor: not-allowed;
}
</style>
